import {
  BoardState,
  Color,
  GameVariant,
  KingMoveStreak,
  Piece,
  PieceType,
  Square,
  VARIANT_SPECS,
} from '@draughts/shared';

/**
 * Lo stato della regola Frisone «dama bloccata» per UN colore.
 */
export interface BlockedKingState {
  /** La casella della dama che ha accumulato le mosse silenziose. */
  square: Square;
  /**
   * Quante mosse silenziose di fila ha gia' fatto. Il predicato riporta la striscia come
   * l'ha contata il motore e non la cappa da se': cappare e' una decisione di chi
   * DISEGNA, non della regola. Con la regola in vigore non puo' superare `limit` — al
   * tetto la dama e' ferma, e qualunque cosa la sblocchi (una cattura, il movimento di un
   * altro pezzo) azzera la striscia.
   */
  count: number;
  /** Il tetto della variante (`rules.blockedKingMaxMoves`): 3 su Frisone e Frysk!. */
  limit: number;
  /** `count >= limit`: la dama non puo' piu' muovere senza catturare. */
  blocked: boolean;
}

/**
 * Lo stato della regola «dama bloccata» per un colore, o `null` se in questa posizione
 * la regola non dice niente su di lui.
 *
 * UNA SOLA VERITA' PER DUE CONSUMATORI. Queste condizioni stavano dentro
 * `applyBlockedKingRule`, che le teneva private: la generazione di mosse era l'unico
 * posto che ne aveva bisogno. Poi la damiera a schermo ha dovuto disegnare i pallini
 * sotto la dama, e una copia delle condizioni nella UI avrebbe derivato dalla regola
 * vera — e' esattamente cio' che e' successo a `minimax.ts`, che duplica
 * `determineGameStatus` ed e' andato a fondo in silenzio su piu' di una variante.
 *
 * `null` copre quattro casi:
 *   1. la variante non ha la regola (`blockedKingMaxMoves <= 0`): 26 varianti su 28;
 *   2. quel colore non ha una striscia in corso;
 *   3. ESENZIONE: quel colore non ha piu' PEDINE;
 *   4. sulla casella della striscia non c'e' piu' una dama di quel colore.
 *
 * L'ESENZIONE E' SULLE PEDINE, NON SUL NUMERO DI DAME. Art. 17 del regolamento ufficiale
 * (`docs/OfficialRulesFrisianDraughts2011.pdf`): «A player with one or more kings AND MEN
 * may not make more than three consecutive simple moves with the same king. […] During the
 * endgame, if a player only has one or more kings then he is not bound by this rule.»
 * Quindi vale con UNA dama esattamente come con due. Qui si chiedeva anche `>= 2 dame` —
 * una lettura sbagliata di «one or more kings» — e sulla dama sola la damiera accendeva
 * tre pallini per un divieto che non esiste. Lo «during the endgame» non chiede codice in
 * piu': «zero pedine» E' la condizione di finale.
 *
 * L'esenzione vale anche a conteggio 1-2, dove il filtro delle mosse non la
 * guarderebbe nemmeno: la' la regola non mordera' MAI, e dei pallini accesi
 * mentirebbero.
 *
 * PRENDE IL COLORE e non legge `currentTurn`: la generazione di mosse chiede del colore
 * di turno, la damiera a schermo li vuole entrambi — anche la dama avversaria conta le
 * sue mosse.
 *
 * PRENDE `pieces`/`variant`/`kingMoveStreak` SCIOLTI e non una `BoardState`: durante la
 * revisione la UI ha i pezzi di un ply passato e la striscia di quel ply, ma non una
 * damiera, e fabbricarne una mescolando `moveHistory` vivo con pezzi di ieri sarebbe
 * una damiera che non e' mai esistita. Cosi' la firma dice la verita' su cio' che la
 * regola legge davvero.
 */
export function getBlockedKingState(
  pieces: readonly Piece[],
  variant: GameVariant,
  color: Color,
  kingMoveStreak: BoardState['kingMoveStreak'],
): BlockedKingState | null {
  const limit = VARIANT_SPECS[variant]?.rules.blockedKingMaxMoves;
  if (!limit || limit <= 0) return null;

  const streak: KingMoveStreak | null | undefined = kingMoveStreak?.[color];
  if (!streak) return null;

  // Un solo giro sui pezzi cerca una pedina (l'esenzione) e la dama sulla casella della
  // striscia. Prima erano due `.filter()` piu' un `.some()`, e questo codice gira a ogni
  // nodo della ricerca dell'IA, dove le allocazioni si sentono. Non serve CONTARE: basta
  // sapere se una pedina c'e'.
  let hasMan = false;
  let kingOnStreakSquare = false;
  for (const piece of pieces) {
    if (piece.color !== color) continue;
    if (piece.type === PieceType.MAN) {
      hasMan = true;
    } else if (piece.square === streak.square) {
      kingOnStreakSquare = true;
    }
  }

  if (!hasMan) return null;
  if (!kingOnStreakSquare) return null;

  return {
    square: streak.square,
    count: streak.count,
    limit,
    blocked: streak.count >= limit,
  };
}
